import { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import FooterSection from "../home/components/FooterSection";
import { supabase } from "../../lib/supabase";

interface SearchedProfile {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  joined_at: string;
}

export default function UserSearchPage() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchedProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    const { data } = await supabase
      .from("profiles")
      .select("id, username, display_name, avatar_url, bio, joined_at")
      .or(`username.ilike.%${trimmed}%,display_name.ilike.%${trimmed}%`)
      .order("username", { ascending: true })
      .limit(30);
    setResults((data ?? []) as SearchedProfile[]);
    setLoading(false);
  }, []);

  const handleChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(value);
    }, 350);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    doSearch(query);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <div className="pt-16">
        {/* Hero Search Area */}
        <div className="w-full py-14 md:py-20 px-4" style={{ background: "linear-gradient(135deg, #064e3b 0%, #059669 60%, #57534e 100%)" }}>
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-5">
              <i className="ri-user-search-line text-white text-2xl" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Kullanıcı Ara</h1>
            <p className="text-white/70 text-sm mb-8">İsim veya kullanıcı adıyla Bursa gezginlerini bul</p>

            <form onSubmit={handleSubmit} className="relative">
              <div className="flex items-center bg-white rounded-2xl overflow-hidden">
                <div className="pl-4 flex items-center">
                  <i className="ri-search-line text-stone-400 text-lg" />
                </div>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => handleChange(e.target.value)}
                  placeholder="Kullanıcı adı veya isim ara..."
                  className="flex-1 px-3 py-4 text-sm text-stone-800 bg-transparent outline-none placeholder:text-stone-400"
                  autoFocus
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => { setQuery(""); setResults([]); setSearched(false); }}
                    className="pr-2 flex items-center cursor-pointer text-stone-400 hover:text-stone-600"
                  >
                    <i className="ri-close-circle-fill text-lg" />
                  </button>
                )}
                <button
                  type="submit"
                  className="mx-2 px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold rounded-xl cursor-pointer transition-colors whitespace-nowrap"
                >
                  Ara
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Results */}
        <div className="max-w-2xl mx-auto px-4 py-8">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-stone-400 text-sm">Aranıyor...</p>
              </div>
            </div>
          )}

          {!loading && !searched && (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-4">
                <i className="ri-group-line text-stone-300 text-3xl" />
              </div>
              <p className="text-stone-400 text-sm">Aramak istediğiniz kullanıcı adını veya ismi girin</p>
            </div>
          )}

          {!loading && searched && results.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-4">
                <i className="ri-user-unfollow-line text-stone-300 text-3xl" />
              </div>
              <p className="text-stone-600 text-sm font-medium">Kullanıcı bulunamadı</p>
              <p className="text-stone-400 text-xs mt-1">
                &ldquo;<span className="font-medium">{query}</span>&rdquo; ile eşleşen kullanıcı yok
              </p>
            </div>
          )}

          {!loading && results.length > 0 && (
            <div>
              <p className="text-xs text-stone-400 mb-4 font-medium uppercase tracking-wide">
                {results.length} kullanıcı bulundu
              </p>
              <div className="space-y-2">
                {results.map((p) => {
                  const name = p.display_name || p.username;
                  const initials = name.charAt(0).toUpperCase();
                  return (
                    <Link
                      key={p.id}
                      to={`/profile/${p.username}`}
                      className="flex items-center gap-4 bg-white border border-stone-200 rounded-2xl p-4 hover:border-emerald-300 hover:bg-emerald-50/20 transition-colors cursor-pointer group"
                    >
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {p.avatar_url ? (
                          <img
                            src={p.avatar_url}
                            alt={name}
                            className="w-12 h-12 rounded-full object-cover object-top border-2 border-stone-100"
                          />
                        ) : (
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center border-2 border-stone-100"
                            style={{ background: "linear-gradient(135deg, #10b981 0%, #065f46 100%)" }}
                          >
                            <span className="text-white font-bold text-lg">{initials}</span>
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-stone-900 text-sm group-hover:text-emerald-800 transition-colors">{name}</p>
                        <p className="text-xs text-stone-500">@{p.username}</p>
                        {p.bio && (
                          <p className="text-xs text-stone-400 mt-1 line-clamp-1">{p.bio}</p>
                        )}
                      </div>

                      {/* Member since */}
                      <div className="flex-shrink-0 text-right hidden sm:block">
                        <p className="text-xs text-stone-400">
                          {new Date(p.joined_at).getFullYear()} üyesi
                        </p>
                      </div>

                      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line text-stone-400 group-hover:text-emerald-600 transition-colors" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <FooterSection />
    </div>
  );
}
